// /forgot-password — pantalla para iniciar el recovery flow. El usuario
// ingresa su email, Supabase manda link de reset que aterriza en
// /reset-password con tokens en la URL hash.
//
// Sub-decisión Sprint 2: NO revelamos si el email existe o no en
// `auth.users` (security best practice — evita enumerar usuarios).
// Mensaje genérico post-submit: "Si el email está registrado, vas a
// recibir un link en breve."

import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Loader2, Mail } from "lucide-react";
import { supabase } from "@/lib/supabase/client";
import { AuthLayout } from "@/app/components/auth/AuthLayout";
import { Button } from "@/app/components/ui/button";
import { Input } from "@/app/components/ui/input";

type Mode = "idle" | "submitting" | "sent";

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [mode, setMode] = useState<Mode>("idle");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim()) {
      setError("Ingresá tu email");
      return;
    }

    setMode("submitting");
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(
      email.trim(),
      {
        redirectTo: `${window.location.origin}/reset-password`,
      },
    );

    if (resetError) {
      const msg = resetError.message.toLowerCase();
      if (msg.includes("rate limit") || msg.includes("too many")) {
        setError("Intentaste muchas veces. Esperá unos minutos.");
      } else {
        setError(resetError.message);
      }
      setMode("idle");
      return;
    }

    setMode("sent");
  };

  if (mode === "sent") {
    return (
      <AuthLayout
        title="Revisá tu email"
        subtitle="Si el email está registrado, te enviamos un link para crear una contraseña nueva."
        footer={
          <Link
            to="/login"
            className="text-primary hover:underline font-medium"
          >
            Volver al login
          </Link>
        }
      >
        <div className="flex items-center justify-center py-4">
          <Mail className="w-12 h-12 text-primary" strokeWidth={1.5} />
        </div>
        <p className="text-sm text-muted-foreground text-center">
          El link es válido por 24 horas. Si no lo ves en unos minutos, revisá
          spam.
        </p>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Recuperar contraseña"
      subtitle="Te enviamos un link a tu email para crear una contraseña nueva."
      footer={
        <Link
          to="/login"
          className="text-primary hover:underline font-medium"
        >
          Volver al login
        </Link>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium mb-1.5"
          >
            Email
          </label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="tu@email.com"
            disabled={mode === "submitting"}
            aria-invalid={!!error}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-900 border border-red-200"
          >
            {error}
          </div>
        )}

        <Button
          type="submit"
          className="w-full"
          disabled={mode === "submitting"}
        >
          {mode === "submitting" ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Enviando…
            </>
          ) : (
            "Enviar link"
          )}
        </Button>
      </form>
    </AuthLayout>
  );
}
